import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import firebase from "firebase/compat/app";

import { ReactComponent as LogoSVG } from "resources/logos/Aturna_Brandmark_RGB_Purple.svg";
import { ReactComponent as Logo2SVG } from "static/logos/logo-main.svg";

import styles from "./styles.module.scss";
import DataLayerContext from "contexts/DataLayerContext";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  EventName,
  SignupEngagementAction,
} from "contexts/DataLayerContext/types";
import GoogleAuthProvider = firebase.auth.GoogleAuthProvider;
import validatePasswordStrength from "../../../../utils/password";
import {
  navigateToGlobalLanding,
  navigateToPartnerLanding,
  redirect,
} from "../../../../modules/navigation/helpers/navigator";
import {
  getRoute,
  ROUTE_TEMPLATE,
} from "../../../../modules/navigation/routes";
import { useDispatch } from "react-redux";
import { getFormattedPartnerId } from "modules/navigation/helpers/getFormattedPartnerId";

type SignupWrapperProps = {
  authProvider: firebase.auth.Auth;
  userJurisdiction?: string;
};

const SignupWrapper = ({
  authProvider,
  userJurisdiction,
}: SignupWrapperProps) => {
  const { sendEvent } = useContext(DataLayerContext);
  const [hasSentAttemptEvent, setHasSentAttemptEvent] = useState(false);
  const [hasSentSignupErrorEvent, setHasSentSignupErrorEvent] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTermsOfUse, setAgreeToTermsOfUse] = useState(false);
  const [registerEmailError, setRegisterEmailError] = useState(false);
  const [registerPasswordError, setRegisterPasswordError] = useState(false);
  const [registerConfirmPasswordError, setRegisterConfirmPasswordError] =
    useState(false);
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState("");
  const [passwordInstructionsVisible, setPasswordInstructionsVisible] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // React States
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);

  const handleClickLogo = () => {
    const partnerId = getFormattedPartnerId();

    if (partnerId) {
      navigateToPartnerLanding(dispatch, partnerId);
    } else {
      navigateToGlobalLanding(dispatch);
    }
  };

  const googleAuthProvider = new GoogleAuthProvider();

  const NO_ERROR_MESSAGE = "";

  const DEFAULT_ERROR_MESSAGE =
    "An error has occurred, please check your details and try again.";

  const GOOGLE_AGREE_ERROR_MESSAGE =
    "Please confirm you agree to the terms and conditions and privacy policy.";

  const GOOGLE_SIGNUP_ERROR_MESSAGE =
    "An error occurred signing up to Google, please try again";

  const isErrorState = useCallback((): boolean => {
    return errorMessage !== "";
  }, [errorMessage]);

  if (!hasSentAttemptEvent && sendEvent !== undefined) {
    sendEvent({
      event: EventName.SignupEngagement,
      action: SignupEngagementAction.SIGNUP_ATTEMPT,
      user_State: userJurisdiction,
    });
    setHasSentAttemptEvent(true);
  }

  const sendErrorEvent = useCallback(
    (errorField?: string) => {
      if (!hasSentSignupErrorEvent && sendEvent !== undefined) {
        sendEvent({
          event: EventName.SignupEngagement,
          action: SignupEngagementAction.SIGNUP_ERROR,
          signup_error_field: errorField,
          user_State: userJurisdiction,
        });
        setHasSentSignupErrorEvent(true);
      }
    },
    [
      hasSentSignupErrorEvent,
      setHasSentSignupErrorEvent,
      sendEvent,
      userJurisdiction,
    ],
  );

  const signupUserUsingEmailPassword = async (
    email: string,
    password: string,
  ): Promise<void> => {
    await authProvider
      .createUserWithEmailAndPassword(email, password)
      .catch(() => {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
      });
  };

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    await signupUserUsingEmailPassword(registerEmail, registerPassword);
    setAgreeToTermsOfUse(false);
  };

  const signupWithGoogle = async () => {
    setErrorMessage(NO_ERROR_MESSAGE);
    googleAuthProvider.addScope("email");
    await authProvider
      .signInWithPopup(googleAuthProvider)
      .then(() => {})
      .catch(() => {
        setErrorMessage(GOOGLE_SIGNUP_ERROR_MESSAGE);
      });
  };

  const handleGoogle = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (!agreeToTermsOfUse) {
      setErrorMessage(GOOGLE_AGREE_ERROR_MESSAGE);
    } else {
      await signupWithGoogle();
      setAgreeToTermsOfUse(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleAgreedToTermOfUse = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAgreeToTermsOfUse(checked);
    setErrorMessage(NO_ERROR_MESSAGE);
  };

  const dispatch = useDispatch();
  const handleNavigateToLoginScreen = () => {
    redirect(dispatch, getRoute(ROUTE_TEMPLATE.LOGIN));
  };

  useEffect(() => {
    if (isErrorState()) {
      sendErrorEvent();
    }
  }, [isErrorState, sendErrorEvent]);

  useEffect(() => {
    const unregisterSignupObserver = firebase
      .auth()
      .onAuthStateChanged((user) => {
        if (user !== null && sendEvent !== undefined) {
          // User has logged in or registered
          sendEvent({
            event: EventName.SignupEngagement,
            action: SignupEngagementAction.SIGNUP_SUCCESS,
            user_State: userJurisdiction,
            user_ID: user.uid,
          });
        }
      });

    return () => unregisterSignupObserver();
  });

  useEffect(() => {
    setSubmitButtonEnabled(
      registerEmail.length !== 0 &&
        registerPassword.length !== 0 &&
        registerConfirmPassword.length !== 0 &&
        !registerEmailError &&
        !registerPasswordError &&
        registerConfirmPassword === registerPassword &&
        agreeToTermsOfUse,
    );
  }, [
    registerEmail,
    registerPassword,
    registerConfirmPassword,
    registerEmailError,
    registerPasswordError,
    agreeToTermsOfUse,
  ]);

  const handleRegisterEmailChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const emailAddress = e.target.value;
    validateEmail(emailAddress, setRegisterEmailError);
    setRegisterEmail(emailAddress);
    setErrorMessage(NO_ERROR_MESSAGE);
  };

  type ErrorSetter = (error: boolean) => void;

  const validateEmail = (currentEmail: string, errorSetter: ErrorSetter) => {
    if (currentEmail.length === 0) {
      errorSetter(true);
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,32}[.]?$/i.test(currentEmail)
    ) {
      errorSetter(true);
    } else {
      errorSetter(false);
    }
  };

  const handleRegisterPasswordChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const password = e.target.value;
    const isValid = validatePasswordStrength(password);
    setRegisterPasswordError(!isValid);
    setPasswordInstructionsVisible(!isValid);
    setRegisterPassword(password);
    setRegisterConfirmPasswordError(registerConfirmPassword !== password);
    setErrorMessage(NO_ERROR_MESSAGE);
  };

  const handleRegisterConfirmPasswordChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const confirmPassword = e.target.value;
    setRegisterConfirmPassword(confirmPassword);
    setRegisterConfirmPasswordError(registerPassword !== confirmPassword);
    setErrorMessage(NO_ERROR_MESSAGE);
  };

  const url = window.location.hostname;
  const hostname = url.replace(/^(app\.)/, "");
  const baseUrl = "https://" + hostname;
  const termsOfUseLink = baseUrl + "/terms";
  const privacyPolicyLink = baseUrl + "/privacy-policy";

  return (
    <Box component="div" className={styles.login}>
      <Box component="div" className={styles.login__image}>
        <div
          onClick={handleClickLogo}
          className={styles["login__logo-1-container"]}
        >
          <LogoSVG
            aria-label="Logo"
            className={styles["login__image__logo-1"]}
          />
        </div>

        <h2>Sign up for an account</h2>
        <h3>
          Already have an account?{" "}
          <Link id="signInLink" onClick={handleNavigateToLoginScreen}>
            Sign in here
          </Link>
        </h3>

        <Logo2SVG
          aria-label="Logo"
          className={styles["login__image__logo-2"]}
        />

        <form>
          <FormGroup className={styles.login__form}>
            <FormControl
              className={styles.login__input_container}
              variant="outlined"
            >
              <InputLabel htmlFor="ui-register-email-input">Email</InputLabel>
              <OutlinedInput
                className={styles.login__input_input}
                id="ui-register-email-input"
                name="ui-register-email-input"
                type={"text"}
                label={"Email"}
                error={registerEmailError}
                onChange={(e) => handleRegisterEmailChange(e)}
              />
            </FormControl>

            <FormControl
              className={styles.login__input_container}
              variant="outlined"
            >
              <InputLabel htmlFor="ui-register-password-input">
                Password
              </InputLabel>
              <OutlinedInput
                className={styles.login__input_input}
                id="ui-register-password-input"
                name="ui-register-password-input"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                onChange={(e) => handleRegisterPasswordChange(e)}
                error={registerPasswordError}
              />
            </FormControl>
            <FormControl
              className={styles.login__input_container}
              variant="outlined"
            >
              <InputLabel htmlFor="ui-register-confirm-password-input">
                Confirm Password
              </InputLabel>
              <OutlinedInput
                className={styles.login__input_input}
                id="ui-register-confirm-password-input"
                type={showConfirmPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Confirm Password"
                onChange={(e) => handleRegisterConfirmPasswordChange(e)}
                placeholder="Re-enter password"
                error={registerConfirmPasswordError}
              />
              <Box
                component="div"
                className={styles.login__error_message_container}
                sx={{
                  display: isErrorState() ? "block" : "none",
                }}
              >
                <Typography className={styles.login__error_message}>
                  {errorMessage}
                </Typography>
              </Box>
              <Box
                component="div"
                id="ui-register-password-instructions"
                sx={{
                  display: passwordInstructionsVisible ? "block" : "none",
                }}
              >
                <Typography className={styles.login__password_instructions}>
                  Your password should:
                </Typography>
                <Typography className={styles.login__password_requirements}>
                  • be at least 8 characters long
                </Typography>
                <Typography className={styles.login__password_requirements}>
                  • contain at least one uppercase and lowercase letter
                </Typography>
                <Typography className={styles.login__password_requirements}>
                  • contain at least one number
                </Typography>
                <Typography className={styles.login__password_requirements}>
                  • contain one of the following symbols: !@#$&*
                </Typography>
              </Box>
            </FormControl>
            <Box component="div" className={styles.login__input_checkbox_div}>
              <Checkbox
                id="agree"
                name="agree"
                checked={agreeToTermsOfUse}
                onChange={(e) => handleAgreedToTermOfUse(e)}
              />
              <label htmlFor="agree">
                I agree to Aturna's{" "}
                <a href={termsOfUseLink} target="_blank" rel="noreferrer">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a href={privacyPolicyLink} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
              </label>
            </Box>

            <Box
              component="div"
              className={styles.login__input_button_container}
            >
              <Button
                variant="contained"
                className={styles.login__submit_button}
                type="submit"
                disabled={!submitButtonEnabled}
                onClick={handleSubmit}
              >
                Sign up
              </Button>
            </Box>

            <Box
              component="div"
              className={styles.login__input_separator_container}
            >
              <Box
                component="div"
                className={styles.login__input_separator_left}
              >
                <hr></hr>
              </Box>
              <Box
                component="div"
                className={styles.login__input_separator_middle}
              >
                OR
              </Box>
              <Box
                component="div"
                className={styles.login__input_separator_right}
              >
                <hr></hr>
              </Box>
            </Box>
            <Box
              component="div"
              className={styles.login__input_google_container}
            >
              <Button
                variant="outlined"
                className={styles.login__google_button}
                type="submit"
                onClick={handleGoogle}
              >
                <div className={styles.login__google_button_content}>
                  <div className={styles.login__gsi_material_button_icon}>
                    <svg
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 48 48"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                    >
                      <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                      ></path>
                      <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                      ></path>
                      <path
                        fill="#FBBC05"
                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                      ></path>
                      <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                      ></path>
                      <path fill="none" d="M0 0h48v48H0z"></path>
                    </svg>
                  </div>
                  <p>Sign up with Google</p>
                </div>
              </Button>
            </Box>
          </FormGroup>
        </form>
      </Box>
    </Box>
  );
};

export default SignupWrapper;
